/* Toma de asistencia de eventos (para tomadores designados).
 *
 * - Lista los eventos donde el usuario es tomador (validado por backend).
 * - Permite buscar un invitado por cédula y registrar su asistencia (presente),
 *   indicando cuántos acompañantes llegaron (hasta el máximo permitido).
 * - Eventos con pedido (requiere_pedido): muestra el detalle del pedido y el
 *   total; registrar la asistencia registra también el RETIRO (comprobante
 *   para el descuento de nómina: solo se descuenta lo retirado).
 * - Eventos con firma (requiere_firma): captura la firma manuscrita antes de
 *   registrar (reemplaza la planilla de papel).
 * - Eventos con QR (requiere_qr): permite escanear el QR dinámico del invitado
 *   en lugar de tipear la cédula (patrón de Entregas Masivas).
 */
import {
  IonButton,
  IonContent,
  IonInput,
  IonPage,
  IonSpinner,
  useIonToast,
} from "@ionic/react";
import { Typography } from "@material-ui/core";
import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import Header from "src/components/Header";
import EventoPresentesLista from "src/components/EventoPresentesLista";
import FirmaCanvas, { FirmaCanvasHandle } from "src/components/FirmaCanvas";
import Eventos from "src/services/eventos";

const SCANNER_ID = "evento-qr-scanner";

const fmtMonto = (n: number | null | undefined) =>
  `Gs. ${Number(n || 0).toLocaleString("es-PY", { maximumFractionDigits: 0 })}`;

const EventoAsistencia: React.FC = () => {
  const [present] = useIonToast();
  const [cargando, setCargando] = useState(true);
  const [eventos, setEventos] = useState<any[]>([]);
  const [eventoId, setEventoId] = useState<number | null>(null);
  const [cedula, setCedula] = useState("");
  const [encontrado, setEncontrado] = useState<any | null>(null);
  const [buscando, setBuscando] = useState(false);
  const [acomp, setAcomp] = useState(0);
  const [presentes, setPresentes] = useState<any[]>([]);
  const [registrando, setRegistrando] = useState(false);

  // Retiro flexible: cuánto de cada ítem retira efectivamente (item_id -> cant).
  // Se inicializa con lo pedido; puede bajar (retiro parcial), subir (quiso
  // llevar más) o sumar ítems del catálogo que no estaban en el pedido.
  const [retiroCant, setRetiroCant] = useState<Record<number, number>>({});
  // Ítems agregados en el momento (no pedidos): toman el precio vigente.
  const [extras, setExtras] = useState<Array<{ item_id: number; nombre: string; precio: number }>>([]);
  // El selector de ítems recién aparece al tocar "Agregar nuevo ítem".
  const [mostrarAgregar, setMostrarAgregar] = useState(false);

  // QR: token escaneado (se envía al registrar para dejar método = QR).
  const [escaneando, setEscaneando] = useState(false);
  const [qrToken, setQrToken] = useState<string | null>(null);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const procesandoRef = useRef(false);

  const firmaRef = useRef<FirmaCanvasHandle>(null);

  const evento = eventos.find((e) => e.evento_id === eventoId) || null;

  useEffect(() => {
    (async () => {
      try {
        const evs = await Eventos.misEventosAsistencia();
        setEventos(evs);
        if (evs.length === 1) {
          setEventoId(evs[0].evento_id);
        }
      } finally {
        setCargando(false);
      }
    })();
    return () => {
      detenerScanner();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (eventoId) cargarPresentes(eventoId);
    limpiarSeleccion();
    detenerScanner();
    setEscaneando(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventoId]);

  const limpiarSeleccion = () => {
    setEncontrado(null);
    setCedula("");
    setQrToken(null);
    setRetiroCant({});
    setExtras([]);
    setMostrarAgregar(false);
  };

  // Fija el invitado encontrado (por cédula o QR) e inicializa el retiro
  // con todo lo pedido.
  const seleccionarInvitado = (inv: any) => {
    setEncontrado(inv);
    setAcomp(inv.acompanantes_presentes ?? inv.acompanantes_confirmados ?? 0);
    const cant: Record<number, number> = {};
    (inv.pedido || []).forEach((l: any) => {
      cant[l.item_id] = l.cantidad;
    });
    setRetiroCant(cant);
    setExtras([]);
    setMostrarAgregar(false);
  };

  const cargarPresentes = async (id: number) => {
    try {
      setPresentes(await Eventos.presentes(id));
    } catch {
      setPresentes([]);
    }
  };

  // ------------------------- Escáner QR -------------------------
  const detenerScanner = async () => {
    try {
      if (scannerRef.current) {
        const s = scannerRef.current;
        scannerRef.current = null;
        // @ts-ignore html5-qrcode expone getState() en runtime
        if (s.getState && s.getState() === 2) {
          await s.stop();
        }
        await s.clear();
      }
    } catch {
      // noop
    }
  };

  useEffect(() => {
    if (!escaneando) {
      detenerScanner();
      return;
    }
    // El div del scanner debe existir en el DOM antes de iniciar la cámara.
    const t = setTimeout(async () => {
      try {
        await detenerScanner();
        const scanner = new Html5Qrcode(SCANNER_ID, false);
        scannerRef.current = scanner;
        procesandoRef.current = false;
        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          (decodedText) => procesarQR(decodedText),
          () => {
            /* errores de frame no nos interesan */
          }
        );
      } catch {
        present({ message: "No se pudo iniciar la cámara", duration: 3500, color: "danger" });
        setEscaneando(false);
      }
    }, 120);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [escaneando]);

  const procesarQR = async (qr: string) => {
    if (procesandoRef.current || !eventoId) return;
    procesandoRef.current = true;
    try {
      const inv = await Eventos.escanearQR(eventoId, qr);
      if (inv) {
        seleccionarInvitado(inv);
        setCedula(String(inv.cedula));
        setQrToken(qr);
        setEscaneando(false);
        await detenerScanner();
        return;
      }
      procesandoRef.current = false;
    } catch (err: any) {
      present({
        message: err?.response?.data?.error || "QR inválido",
        duration: 3000,
        color: "warning",
      });
      // Dejamos seguir escaneando (el QR rota solo).
      setTimeout(() => {
        procesandoRef.current = false;
      }, 1500);
    }
  };

  // ------------------------- Búsqueda por cédula -------------------------
  const buscar = async () => {
    if (!eventoId || !cedula.trim()) return;
    setBuscando(true);
    setEncontrado(null);
    setQrToken(null);
    try {
      const inv = await Eventos.buscarInvitado(eventoId, cedula.trim());
      if (!inv) {
        present({ message: "La cédula no figura como invitada", duration: 3000, color: "warning" });
        return;
      }
      seleccionarInvitado(inv);
    } catch (err: any) {
      present({ message: err?.response?.data?.error || "No se encontró", duration: 3000, color: "danger" });
    } finally {
      setBuscando(false);
    }
  };

  // ------------------------- Registro -------------------------
  const yaRetiro = Boolean(encontrado?.retiro);
  const requiereFirma = Boolean(evento?.requiere_firma) && !yaRetiro;
  const requiereRetiro = Boolean(evento?.requiere_retiro);

  const pedidoLineas: any[] = encontrado?.pedido || [];
  const unidadesRetiro =
    pedidoLineas.reduce((acc, l) => acc + (retiroCant[l.item_id] ?? 0), 0) +
    extras.reduce((acc, e) => acc + (retiroCant[e.item_id] ?? 0), 0);
  const totalRetiro =
    pedidoLineas.reduce((acc, l) => acc + (retiroCant[l.item_id] ?? 0) * Number(l.precio_unitario || 0), 0) +
    extras.reduce((acc, e) => acc + (retiroCant[e.item_id] ?? 0) * Number(e.precio || 0), 0);

  // Ítems del catálogo que todavía se pueden agregar (no pedidos ni ya agregados).
  const catalogoDisponible: any[] = (encontrado?.items_catalogo || []).filter(
    (it: any) =>
      !pedidoLineas.some((l) => l.item_id === it.id) &&
      !extras.some((e) => e.item_id === it.id)
  );

  // Al elegir un ítem del selector se agrega directo (con 1 unidad) y el
  // selector se oculta hasta que vuelvan a tocar "Agregar nuevo ítem".
  const agregarExtra = (itemId: string) => {
    const it = catalogoDisponible.find((c: any) => String(c.id) === itemId);
    if (!it) return;
    setExtras((prev) => [...prev, { item_id: it.id, nombre: it.nombre, precio: Number(it.precio || 0) }]);
    setRetiroCant((prev) => ({ ...prev, [it.id]: 1 }));
    setMostrarAgregar(false);
  };

  const registrar = async () => {
    if (!eventoId || !encontrado) return;

    // Retiro flexible: se envía cuánto retira de cada ítem (menos, más o
    // ítems agregados); el descuento sale solo de lo retirado.
    let itemsRetirados: Array<{ item_id: number; cantidad: number }> | undefined;
    if (requiereRetiro && !yaRetiro && (pedidoLineas.length > 0 || extras.length > 0)) {
      if (unidadesRetiro === 0) {
        present({ message: "Indicá al menos un ítem a retirar", duration: 3000, color: "warning" });
        return;
      }
      itemsRetirados = [
        ...pedidoLineas.map((l) => ({ item_id: l.item_id, cantidad: retiroCant[l.item_id] ?? 0 })),
        ...extras
          .filter((e) => (retiroCant[e.item_id] ?? 0) > 0)
          .map((e) => ({ item_id: e.item_id, cantidad: retiroCant[e.item_id] ?? 0 })),
      ];
    }

    let firma: string | undefined;
    if (requiereFirma) {
      if (!firmaRef.current?.tieneTrazos()) {
        present({ message: "Falta la firma de la persona", duration: 3000, color: "warning" });
        return;
      }
      firma = firmaRef.current.obtenerPng();
      if (!firma || firma.length < 50) {
        present({ message: "No se pudo capturar la firma, reintentá", duration: 3000, color: "warning" });
        return;
      }
    }

    setRegistrando(true);
    try {
      const resp = await Eventos.registrarAsistencia(eventoId, encontrado.cedula, acomp, {
        firma_b64: firma,
        qr: qrToken || undefined,
        items_retirados: itemsRetirados,
      });
      present({
        message: resp?.info
          ? `${resp.info}: ${encontrado.nombre || encontrado.cedula}`
          : `Registrado: ${encontrado.nombre || encontrado.cedula}`,
        duration: 3500,
        color: resp?.retiro ? "warning" : "success",
      });
      limpiarSeleccion();
      await cargarPresentes(eventoId);
    } catch (err: any) {
      present({ message: err?.response?.data?.error || "No se pudo registrar", duration: 4000, color: "danger" });
    } finally {
      setRegistrando(false);
    }
  };

  if (cargando) {
    return (
      <IonPage>
        <Header titulo="Asistencia" />
        <IonContent id="eventos-asistencia">
          <div style={{ display: "flex", justifyContent: "center", padding: 40 }}><IonSpinner /></div>
        </IonContent>
      </IonPage>
    );
  }

  if (eventos.length === 0) {
    return (
      <IonPage>
        <Header titulo="Asistencia" />
        <IonContent id="eventos-asistencia">
          <div style={{ padding: 24, textAlign: "center" }}>
            <Typography color="textSecondary">No tenés eventos asignados para tomar asistencia.</Typography>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <Header titulo="Asistencia" />
      <IonContent id="eventos-asistencia">
        <div style={{ padding: 16 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ fontSize: 14, color: "#666" }}>Evento</span>
            <select
              value={eventoId ?? ""}
              onChange={(e) => setEventoId(Number(e.target.value))}
              style={{ fontSize: 15, padding: "8px 10px", border: "1px solid #c4c4c4", borderRadius: 6, background: "#fff", color: "#000" }}
            >
              <option value="" disabled>Seleccionar</option>
              {eventos.map((e) => (
                <option key={e.evento_id} value={e.evento_id}>{e.tema}</option>
              ))}
            </select>
          </div>

          {eventoId && (
            <>
              <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                <IonInput
                  placeholder="Cédula del invitado"
                  inputmode="numeric"
                  value={cedula}
                  onIonChange={(e) => setCedula(e.detail.value || "")}
                  style={{ border: "1px solid #ddd", borderRadius: 6, flex: 1, paddingLeft: 8 }}
                />
                <IonButton onClick={buscar} disabled={buscando}>{buscando ? "..." : "Buscar"}</IonButton>
              </div>

              {Boolean(evento?.requiere_qr) && (
                <IonButton
                  expand="block"
                  fill={escaneando ? "outline" : "solid"}
                  color="tertiary"
                  style={{ marginTop: 10 }}
                  onClick={() => {
                    limpiarSeleccion();
                    setEscaneando((v) => !v);
                  }}
                >
                  {escaneando ? "Detener escáner" : "📷 Escanear QR del invitado"}
                </IonButton>
              )}

              {escaneando && (
                <div
                  id={SCANNER_ID}
                  style={{ width: "100%", marginTop: 10, borderRadius: 8, overflow: "hidden" }}
                />
              )}

              {encontrado && (
                <div style={{ background: "#fff", borderRadius: 8, boxShadow: "0 1px 3px rgba(0,0,0,.15)", padding: 14, marginTop: 14 }}>
                  <Typography variant="subtitle1" style={{ fontWeight: 700 }}>{encontrado.nombre || encontrado.cedula}</Typography>
                  <Typography variant="body2" color="textSecondary">CI {encontrado.cedula} · {encontrado.cargo || ""}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Confirmación: {encontrado.estado_confirmacion}
                    {encontrado.es_reconocido ? " · ★ Reconocido" : ""}
                    {qrToken ? " · vía QR" : ""}
                  </Typography>
                  {encontrado.segmento_nombre && (
                    <Typography variant="body2" color="textSecondary">
                      Segmento: {encontrado.segmento_nombre}
                    </Typography>
                  )}

                  {/* Pedido del invitado (eventos con requiere_pedido).
                      Al retirar se elige cuánto de cada ítem se lleva realmente:
                      puede ser menos (retiro parcial), más (quiso llevar otro) o
                      ítems agregados en el momento. Se descuenta lo retirado. */}
                  {Boolean(evento?.requiere_pedido) && (
                    <div style={{ background: "#f5f5f5", borderRadius: 8, padding: 10, marginTop: 10 }}>
                      <Typography variant="subtitle2" style={{ fontWeight: 700 }}>Pedido</Typography>
                      {pedidoLineas.length === 0 && extras.length === 0 && (
                        <Typography variant="body2" color="textSecondary">Sin pedido registrado.</Typography>
                      )}

                      {pedidoLineas.map((l: any, i: number) => (
                        <div
                          key={`p-${i}`}
                          style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginTop: 4 }}
                        >
                          <Typography variant="body2">
                            {l.cantidad === 0
                              ? `${l.nombre} (agregado al retiro)`
                              : `${l.cantidad}x ${l.nombre} — ${fmtMonto(l.subtotal)}`}
                          </Typography>
                          {yaRetiro ? (
                            l.cantidad_retirada !== null && l.cantidad_retirada !== undefined ? (
                              <span
                                style={{
                                  fontSize: 12,
                                  fontWeight: 700,
                                  color: l.cantidad_retirada < l.cantidad ? "#b26a00" : "#2e7d32",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                retiró {l.cantidad_retirada}
                              </span>
                            ) : null
                          ) : (
                            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                              <span style={{ fontSize: 12, color: "#666" }}>Retira:</span>
                              <IonButton
                                size="small"
                                fill="outline"
                                disabled={(retiroCant[l.item_id] ?? 0) === 0}
                                onClick={() => setRetiroCant((prev) => ({ ...prev, [l.item_id]: Math.max(0, (prev[l.item_id] ?? 0) - 1) }))}
                              >−</IonButton>
                              <span style={{ minWidth: 18, textAlign: "center", fontWeight: 700 }}>{retiroCant[l.item_id] ?? 0}</span>
                              <IonButton
                                size="small"
                                fill="outline"
                                disabled={(retiroCant[l.item_id] ?? 0) >= 99}
                                onClick={() => setRetiroCant((prev) => ({ ...prev, [l.item_id]: Math.min(99, (prev[l.item_id] ?? 0) + 1) }))}
                              >+</IonButton>
                            </div>
                          )}
                        </div>
                      ))}

                      {/* Ítems agregados en el momento (precio vigente del catálogo) */}
                      {!yaRetiro && extras.map((e) => (
                        <div
                          key={`e-${e.item_id}`}
                          style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginTop: 4 }}
                        >
                          <Typography variant="body2">
                            {e.nombre} — {fmtMonto(e.precio)}{" "}
                            <span style={{ color: "#1565c0", fontSize: 12, fontWeight: 700 }}>(agregado)</span>
                          </Typography>
                          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                            <span style={{ fontSize: 12, color: "#666" }}>Retira:</span>
                            <IonButton
                              size="small"
                              fill="outline"
                              disabled={(retiroCant[e.item_id] ?? 0) === 0}
                              onClick={() => setRetiroCant((prev) => ({ ...prev, [e.item_id]: Math.max(0, (prev[e.item_id] ?? 0) - 1) }))}
                            >−</IonButton>
                            <span style={{ minWidth: 18, textAlign: "center", fontWeight: 700 }}>{retiroCant[e.item_id] ?? 0}</span>
                            <IonButton
                              size="small"
                              fill="outline"
                              disabled={(retiroCant[e.item_id] ?? 0) >= 99}
                              onClick={() => setRetiroCant((prev) => ({ ...prev, [e.item_id]: Math.min(99, (prev[e.item_id] ?? 0) + 1) }))}
                            >+</IonButton>
                          </div>
                        </div>
                      ))}

                      {/* Agregar un ítem del catálogo que no estaba en el pedido:
                          primero el botón; al tocarlo aparece el selector y al
                          elegir un ítem se agrega directo. */}
                      {!yaRetiro && catalogoDisponible.length > 0 && (
                        mostrarAgregar ? (
                          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8 }}>
                            <select
                              value=""
                              onChange={(e) => agregarExtra(e.target.value)}
                              style={{ flex: 1, minWidth: 0, fontSize: 14, padding: "6px 8px", border: "1px solid #c4c4c4", borderRadius: 6, background: "#fff", color: "#000" }}
                            >
                              <option value="" disabled>Seleccioná el ítem…</option>
                              {catalogoDisponible.map((it: any) => (
                                <option key={it.id} value={String(it.id)}>
                                  {it.nombre} — {fmtMonto(it.precio)}
                                </option>
                              ))}
                            </select>
                            <IonButton size="small" fill="clear" color="medium" onClick={() => setMostrarAgregar(false)}>
                              Cancelar
                            </IonButton>
                          </div>
                        ) : (
                          <IonButton
                            size="small"
                            fill="outline"
                            style={{ marginTop: 8 }}
                            onClick={() => setMostrarAgregar(true)}
                          >
                            + Agregar nuevo ítem
                          </IonButton>
                        )
                      )}

                      {(pedidoLineas.length > 0 || extras.length > 0) && (
                        <>
                          {yaRetiro ? (
                            <>
                              <Typography variant="body2" style={{ fontWeight: 700, marginTop: 6 }}>
                                Total descontado: {fmtMonto(encontrado.retiro?.total_retirado ?? encontrado.pedido_total)}
                              </Typography>
                              {(encontrado.pedido_cuotas ?? 1) > 1 && (
                                <Typography variant="caption" color="textSecondary">
                                  En {encontrado.pedido_cuotas} cuotas de aprox.{" "}
                                  {fmtMonto(Math.round(Number(encontrado.retiro?.total_retirado ?? encontrado.pedido_total ?? 0) / encontrado.pedido_cuotas))} c/u.
                                </Typography>
                              )}
                            </>
                          ) : (
                            <>
                              <Typography variant="body2" style={{ fontWeight: 700, marginTop: 6 }}>
                                Total a descontar: {fmtMonto(totalRetiro)}
                              </Typography>
                              {(encontrado.pedido_cuotas ?? 1) > 1 && (
                                <Typography variant="caption" color="textSecondary" style={{ display: "block" }}>
                                  Eligió descuento en {encontrado.pedido_cuotas} cuotas
                                  {totalRetiro > 0
                                    ? ` (aprox. ${fmtMonto(Math.round(totalRetiro / encontrado.pedido_cuotas))} c/u)`
                                    : ""}.
                                </Typography>
                              )}
                              {totalRetiro < Number(encontrado.pedido_total || 0) && (
                                <Typography variant="caption" style={{ color: "#b26a00", fontWeight: 600 }}>
                                  Retiro parcial: pidió {fmtMonto(encontrado.pedido_total)} pero se
                                  descontará solo lo retirado.
                                </Typography>
                              )}
                              {totalRetiro > Number(encontrado.pedido_total || 0) && (
                                <Typography variant="caption" style={{ color: "#1565c0", fontWeight: 600 }}>
                                  Retira más de lo pedido (pidió {fmtMonto(encontrado.pedido_total)}): se
                                  descontará el total efectivamente retirado.
                                </Typography>
                              )}
                            </>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {yaRetiro ? (
                    <Typography variant="body2" style={{ color: "#2e7d32", fontWeight: 700, marginTop: 8 }}>
                      ✓ Retiro ya registrado
                      {encontrado.retiro?.fecha_retiro
                        ? ` (${String(encontrado.retiro.fecha_retiro).replace("T", " ")})`
                        : ""}
                    </Typography>
                  ) : encontrado.presente ? (
                    <Typography variant="body2" style={{ color: "#2e7d32", fontWeight: 700, marginTop: 6 }}>
                      Ya registrado como presente{(encontrado.acompanantes_presentes ?? 0) > 0 ? ` (+${encontrado.acompanantes_presentes})` : ""}
                    </Typography>
                  ) : null}

                  {encontrado.max_acompanantes > 0 && (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                      <span style={{ fontSize: 14 }}>Acompañantes presentes:</span>
                      <select
                        value={acomp}
                        onChange={(e) => setAcomp(Number(e.target.value))}
                        style={{ fontSize: 15, padding: "6px 10px", border: "1px solid #c4c4c4", borderRadius: 6, background: "#fff", color: "#000" }}
                      >
                        {Array.from({ length: (encontrado.max_acompanantes ?? 0) + 1 }, (_, i) => i).map((n) => (
                          <option key={n} value={n}>{n}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Firma (eventos con requiere_firma, mientras no haya retirado) */}
                  {requiereFirma && (
                    <div style={{ marginTop: 12 }}>
                      <Typography variant="body2" style={{ fontWeight: 700, marginBottom: 4 }}>
                        Firma de {encontrado.nombre || "la persona"}:
                      </Typography>
                      <FirmaCanvas ref={firmaRef} alto={150} />
                      <IonButton size="small" fill="clear" color="medium" onClick={() => firmaRef.current?.limpiar()}>
                        Limpiar firma
                      </IonButton>
                    </div>
                  )}

                  {!yaRetiro && (
                    <IonButton expand="block" color="success" style={{ marginTop: 10 }} onClick={registrar} disabled={registrando}>
                      {registrando
                        ? "Registrando..."
                        : requiereRetiro
                          ? "Registrar retiro"
                          : encontrado.presente
                            ? "Actualizar asistencia"
                            : "Registrar asistencia"}
                    </IonButton>
                  )}
                </div>
              )}

              <Typography variant="subtitle2" style={{ fontWeight: 700, marginTop: 20 }}>
                Presentes ({presentes.length})
              </Typography>
              <EventoPresentesLista presentes={presentes} />
            </>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default EventoAsistencia;
